import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getApiBaseUrl } from '@core/services/api-base-url';
import { normalizePageRequest, PageRequest, PagedResponse } from '@core/models/common.model';
import {
  AffiliateResponse,
  AffiliateEligibilityResponse,
  AffiliateListItemResponse,
  AffiliateTransactionHistoryResponse,
  CreateAffiliateRequest,
  UpdateAffiliateRequest,
} from './affiliate.model';

@Injectable({ providedIn: 'root' })
export class AffiliateApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = getApiBaseUrl();

  getAffiliates(pageRequest?: PageRequest, search?: string | null): Observable<PagedResponse<AffiliateListItemResponse>> {
    const normalizedPageRequest = normalizePageRequest(pageRequest);

    return this.http.get<PagedResponse<AffiliateListItemResponse>>(`${this.apiBaseUrl}affiliates`, {
      params: {
        search: search ?? '',
        pageNumber: normalizedPageRequest.pageNumber,
        pageSize: normalizedPageRequest.pageSize,
      },
    });
  }

  getAffiliate(affiliateId: string): Observable<AffiliateResponse> {
    return this.http.get<AffiliateResponse>(`${this.apiBaseUrl}affiliates/${affiliateId}`);
  }

  createAffiliate(request: CreateAffiliateRequest): Observable<AffiliateResponse> {
    return this.http.post<AffiliateResponse>(`${this.apiBaseUrl}affiliates`, request);
  }

  updateAffiliate(affiliateId: string, request: UpdateAffiliateRequest): Observable<AffiliateResponse> {
    return this.http.put<AffiliateResponse>(`${this.apiBaseUrl}affiliates/${affiliateId}`, request);
  }

  deactivateAffiliate(affiliateId: string): Observable<AffiliateResponse> {
    return this.http.put<AffiliateResponse>(`${this.apiBaseUrl}affiliates/${affiliateId}/deactivate`, {});
  }

  activateAffiliate(affiliateId: string): Observable<AffiliateResponse> {
    return this.http.put<AffiliateResponse>(`${this.apiBaseUrl}affiliates/${affiliateId}/activate`, {});
  }

  getAffiliateHistory(affiliateId: string): Observable<AffiliateTransactionHistoryResponse[]> {
    return this.http.get<AffiliateTransactionHistoryResponse[]>(
      `${this.apiBaseUrl}affiliates/${affiliateId}/history`,
    );
  }

  getEligibleAffiliates(interventionTypeId: string): Observable<AffiliateEligibilityResponse[]> {
    return this.http.get<AffiliateEligibilityResponse[]>(`${this.apiBaseUrl}affiliates/eligible`, {
      params: { interventionTypeId },
    });
  }
}
